import { useEffect, useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { toast } from "react-toastify";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";

import Loading from "../components/common/LoaderCommon";
import {
  type WarrantyDetailResponse,
  type WarrantyProductApi,
  useCreateWarrantyClaim,
  useGetWarrantyClaim,
  useGetWarrantyDetail,
  useUpdateWarrantyClaim,
} from "../service/warrantyService";
import { Button } from "../utils/components/ui/button";
import { Input } from "../utils/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../utils/components/ui/select";
import { formatWarrantyDate } from "./warranty/warrantyShared";

type ClaimFormInvoice = {
  salesId: string;
  invoiceNumber: string;
  purchaseDate: string;
  products: ClaimFormProduct[];
};

type ClaimFormProduct = {
  id: string;
  salesProductId: number;
  name: string;
  brand: string;
  claimId?: string;
};

type ClaimMode = "REFUND" | "REPLACEMENT";

const normalizeClaimStatus = (value: string) =>
  value.trim().toUpperCase().includes("CLOSED") ? "Closed" : "Open";

const normalizeWarrantyProduct = (
  product: WarrantyProductApi,
): ClaimFormProduct => ({
  id: String(product.salesProductId),
  salesProductId: product.salesProductId,
  name: product.productName,
  brand: product.brandName,
  claimId: product.claims[0] ? String(product.claims[0].claimId) : undefined,
});

const normalizeWarrantyInvoice = (
  item?: WarrantyDetailResponse,
): ClaimFormInvoice | null => {
  if (!item) {
    return null;
  }

  return {
    salesId: String(item.salesId),
    invoiceNumber: item.invoiceNumber,
    purchaseDate: item.purchaseDate,
    products: item.products.map(normalizeWarrantyProduct),
  };
};

function WarrantyClaimForm({ mode }: { mode: "create" | "edit" }) {
  const navigate = useNavigate();
  const { salesId, salesProductId, claimId } = useParams();
  const isEdit = mode === "edit";

  const { data: warrantyDetail, isLoading: isDetailLoading } =
    useGetWarrantyDetail(salesId);
  const { data: claimData, isLoading: isClaimLoading } = useGetWarrantyClaim(
    isEdit ? claimId : undefined,
  );
  const createClaim = useCreateWarrantyClaim();
  const updateClaim = useUpdateWarrantyClaim(claimId);

  const invoice = useMemo(
    () => normalizeWarrantyInvoice(warrantyDetail),
    [warrantyDetail],
  );
  const product = useMemo(() => {
    if (!invoice) {
      return undefined;
    }

    if (isEdit) {
      return invoice.products.find((item) => item.claimId === claimId);
    }

    return invoice.products.find((item) => item.id === salesProductId);
  }, [claimId, invoice, isEdit, salesProductId]);

  const [claimDate, setClaimDate] = useState("");
  const [claimQuantity, setClaimQuantity] = useState("1");
  const [status, setStatus] = useState("Open");
  const [claimMode, setClaimMode] = useState<ClaimMode>("REPLACEMENT");
  const [paymentMode, setPaymentMode] = useState("Cash");
  const [refNo, setRefNo] = useState("");
  const [roundedAmount, setRoundedAmount] = useState("");
  const [issueDescription, setIssueDescription] = useState("");
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [attachment, setAttachment] = useState("");

  useEffect(() => {
    if (isEdit && claimData) {
      setClaimDate(claimData.claimDate || "");
      setStatus(normalizeClaimStatus(claimData.status));
      setClaimMode(claimData.claimMode || "REPLACEMENT");
      setPaymentMode(claimData.paymentMode || "Cash");
      setRefNo(claimData.refNo || "");
      setRoundedAmount(
        claimData.roundedAmount !== undefined
          ? String(claimData.roundedAmount)
          : "",
      );
      setIssueDescription(claimData.issueDescription || "");
      setResolutionNotes(claimData.resolutionNotes || "");
      setAttachment(claimData.attachment || "");
    }
  }, [claimData, isEdit]);

  if (isDetailLoading || (isEdit && isClaimLoading)) {
    return <Loading />;
  }

  if (!invoice || !product || (isEdit && !claimData)) {
    return <Navigate to="/warranty" replace />;
  }

  const handleSubmit = () => {
    if (!claimDate || !issueDescription.trim()) {
      toast.error("Claim date and issue description are required.");
      return;
    }

    const parsedRoundedAmount = Number(roundedAmount);

    if (
      claimMode === "REFUND" &&
      (!paymentMode.trim() ||
        !refNo.trim() ||
        !roundedAmount ||
        Number.isNaN(parsedRoundedAmount))
    ) {
      toast.error(
        "Payment mode, reference number, and rounded amount are required for refund claims.",
      );
      return;
    }

    const refundFields =
      claimMode === "REFUND"
        ? {
            paymentMode: paymentMode.toUpperCase(),
            refNo,
            roundedAmount: parsedRoundedAmount,
          }
        : {};

    if (isEdit && claimData) {
      updateClaim.mutate(
        {
          claimId: claimData.claimId,
          claimNumber: claimData.claimNumber,
          claimDate,
          status: status.toUpperCase(),
          claimMode,
          ...refundFields,
          issueDescription,
          resolutionNotes,
          attachment,
        },
        {
          onSuccess: () => {
            toast.success("Warranty claim updated.");
            navigate(`/warranty/${invoice.salesId}/claim/${claimData.claimId}`);
          },
        },
      );

      return;
    }

    createClaim.mutate(
      {
        salesProductId: product.salesProductId,
        salesId: Number(invoice.salesId),
        claimDate,
        claimQuantity: Number(claimQuantity) || 0,
        claimMode,
        ...refundFields,
        issueDescription,
        attachment,
      },
      {
        onSuccess: (response: any) => {
          toast.success("Warranty claim created.");

          const createdClaimId =
            response?.claimId ?? response?.data?.claimId ?? response?.id;

          if (createdClaimId) {
            navigate(`/warranty/${invoice.salesId}/claim/${createdClaimId}`);
            return;
          }

          navigate(`/warranty/${invoice.salesId}`);
        },
      },
    );
  };

  return (
    <div className="flex min-h-full flex-col gap-6 pb-4">
      <section className="rounded-[28px] border border-[#DDE7FB] bg-[radial-gradient(circle_at_top_left,rgba(26,94,249,0.12),transparent_28%),linear-gradient(180deg,#FFFFFF_0%,#F5F9FF_100%)] p-6 shadow-[0_18px_50px_-30px_rgba(15,23,42,0.35)] sm:p-8">
        <div className="space-y-4">
          <Link
            to={`/warranty/${invoice.salesId}`}
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition hover:text-slate-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to invoice details
          </Link>
          <div className="inline-flex items-center rounded-full border border-[#CFE0FF] bg-[#EEF4FF] px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-[#1A5EF9]">
            {invoice.invoiceNumber}
          </div>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              {isEdit ? "Warranty Claim Update" : "Warranty Claim Page"}
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-500 sm:text-base">
              {product.name}
            </p>

          </div>
        </div>
      </section>

      <div className="rounded-[28px] border border-[#DDE7FB] bg-white p-6 shadow-[0_18px_40px_-32px_rgba(15,23,42,0.45)] sm:p-8">
        <div className="grid gap-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600">
                Claim Date
              </label>
              <Input
                type="date"
                value={claimDate}
                onChange={(event) => setClaimDate(event.target.value)}
                className="h-12 rounded-xl border-[#E6ECF5] bg-[#F8FAFC]"
              />
            </div>
            {isEdit ? (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600">
                  Status
                </label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="h-12 w-full rounded-xl border-[#E6ECF5] bg-[#F8FAFC]">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Open">Open</SelectItem>
                    <SelectItem value="Closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600">
                  Claim Quantity
                </label>
                <Input
                  type="number"
                  min="0.0001"
                  step="any"
                  value={claimQuantity}
                  onChange={(event) => setClaimQuantity(event.target.value)}
                  className="h-12 rounded-xl border-[#E6ECF5] bg-[#F8FAFC]"
                />
              </div>
            )}
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600">
                Claim Mode
              </label>
              <Select
                value={claimMode}
                onValueChange={(value) => setClaimMode(value as ClaimMode)}
              >
                <SelectTrigger className="h-12 w-full rounded-xl border-[#E6ECF5] bg-[#F8FAFC]">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="REFUND">REFUND</SelectItem>
                  <SelectItem value="REPLACEMENT">REPLACEMENT</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {claimMode === "REFUND" && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600">
                  Payment Mode
                </label>
                <Select value={paymentMode} onValueChange={setPaymentMode}>
                  <SelectTrigger className="h-12 w-full rounded-xl border-[#E6ECF5] bg-[#F8FAFC]">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Bank">Bank</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {claimMode === "REFUND" && (
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600">
                  Reference Number
                </label>
                <Input
                  value={refNo}
                  onChange={(event) => setRefNo(event.target.value)}
                  placeholder="Enter"
                  className="h-12 rounded-xl border-[#E6ECF5] bg-[#F8FAFC]"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600">
                  Rounded Amount
                </label>
                <Input
                  type="number"
                  min="0"
                  step="any"
                  value={roundedAmount}
                  onChange={(event) => setRoundedAmount(event.target.value)}
                  placeholder="Enter"
                  className="h-12 rounded-xl border-[#E6ECF5] bg-[#F8FAFC]"
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600">
              Issue Description
            </label>
            <textarea
              value={issueDescription}
              onChange={(event) => setIssueDescription(event.target.value)}
              placeholder="Enter"
              className="min-h-[132px] w-full rounded-xl border border-[#E6ECF5] bg-[#F8FAFC] px-3 py-3 text-sm text-slate-700 outline-none transition focus:border-[#1A5EF9] focus:ring-4 focus:ring-[#DCE8FF]"
            />
          </div>

          {isEdit && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600">
                Resolution Notes
              </label>
              <textarea
                value={resolutionNotes}
                onChange={(event) => setResolutionNotes(event.target.value)}
                placeholder="Enter"
                className="min-h-[132px] w-full rounded-xl border border-[#E6ECF5] bg-[#F8FAFC] px-3 py-3 text-sm text-slate-700 outline-none transition focus:border-[#1A5EF9] focus:ring-4 focus:ring-[#DCE8FF]"
              />
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600">
              Attachment
            </label>
            <Input
              value={attachment}
              onChange={(event) => setAttachment(event.target.value)}
              placeholder="Attachment path or URL"
              className="h-12 rounded-xl border-[#E6ECF5] bg-[#F8FAFC]"
            />
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between border-t border-[#E8EDF3] pt-6">
          <div className="text-sm text-slate-400">
            Purchase Date: {formatWarrantyDate(invoice.purchaseDate)}
          </div>
          <div className="flex items-center gap-3">
            <Button
              asChild
              type="button"
              variant="outline"
              className="h-11 min-w-28 rounded-xl border-[#BFD1FF] px-6 text-[#1A5EF9] hover:bg-[#EEF4FF] hover:text-[#0E4FDE]"
            >
              <Link
                to={
                  isEdit && claimId
                    ? `/warranty/${invoice.salesId}/claim/${claimId}`
                    : `/warranty/${invoice.salesId}`
                }
              >
                Cancel
              </Link>
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={createClaim.isPending || updateClaim.isPending}
              className="h-11 min-w-28 rounded-xl bg-[#1A5EF9] px-6 text-white hover:bg-[#0E4FDE]"
            >
              {createClaim.isPending || updateClaim.isPending
                ? "Saving..."
                : isEdit
                  ? "Update"
                  : "Submit"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WarrantyClaimForm;
